import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import {
  Drawer,
  Tabs,
  Row,
  Col,
  Input,
  DatePicker,
  Checkbox,
  Button,
  Typography,
  Divider,
  Select,
  Space,
  Spin,
  Tag,
  message,
} from "antd";
import dayjs from "dayjs";
import { SaveOutlined, FilePdfOutlined, UserOutlined } from "@ant-design/icons";
import EditableItemsTable from "./EditableItemsTable";
import "./ClearanceFormPreview.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const COMPANY_NAME = "Baashyaam Constructions Pvt Ltd";
const FORM_TITLE = "Employee Clearance Form";

const CLEARANCE_FORM_PATH = (id) => `/api/exitprocess/${id}/clearance-form/`;
const CLEARANCE_FORM_PDF_PATH = (id) => `/api/exitprocess/${id}/clearance-form/pdf/`;
const EMPLOYEES_PATH = "/api/emp/employees/";

const EXIT_INTERVIEW_TRIGGER_PATH = (id) => `/api/exitprocess/${id}/exit-interview/trigger/`;
const EXIT_INTERVIEW_DETAIL_PATH = (id) => `/api/exitprocess/${id}/exit-interview/`;

const EI_STATUS_COLOR = {
  TRIGGERED: "gold",
  SUBMITTED: "blue",
  REVIEWED: "green",
};

const FINAL_APPROVAL_STATUS_COLOR = {
  PENDING: "gold",
  APPROVED: "green",
  REJECTED: "red",
};

// Same default rows printed on the physical form — the user edits, adds to,
// or removes these once the drawer is open.
const DEFAULT_ITEMS = {
  department_head: [{ sr_no: 1, particulars: "", status: "PENDING" }],
  it: [
    { sr_no: 1, particulars: "Assets (Software, Hardware, Manuals, Laptop) handled by employee", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 2, particulars: "Mail Access – Need Back Up (Y/N)", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 3, particulars: "Internet Access Disabled (Y/N)", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 4, particulars: "Other Applications Disabled (Y/N)", status: "NOT_RETURNED", remarks: "" },
  ],
  finance: [
    { sr_no: 1, item_name: "Loans/Advance", amount_to_be_deducted: 0, remarks: "" },
    { sr_no: 2, item_name: "Travel Advance", amount_to_be_deducted: 0, remarks: "" },
    { sr_no: 3, item_name: "Others", amount_to_be_deducted: 0, remarks: "" },
  ],
  hr: [
    { sr_no: 1, particulars: "Access Card Tokens Handed Over", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 2, particulars: "Keys Handed Over", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 3, particulars: "Identity Cards", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 4, particulars: "Other Accesses", status: "NOT_RETURNED", remarks: "" },
    { sr_no: 5, particulars: "Sim Card / Mobile", status: "NOT_RETURNED", remarks: "" },
  ],
};

const RETURN_OPTIONS = [
  { value: "RETURNED", label: "Returned" },
  { value: "NOT_RETURNED", label: "Not Returned" },
  { value: "NOT_APPLICABLE", label: "Not Applicable" },
];

const ITEM_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
];

// Maps each clearance section to its clearance_type value as used in
// approval_logs, so we can fall back to the logs when a clearance object
// doesn't carry its approver inline.
const CLEARANCE_TYPE_MAP = {
  department_head: "DEPARTMENT_HEAD",
  it: "IT",
  finance: "FINANCE",
  hr: "HR",
};

const STATUS_TAG_COLOR = {
  PENDING: "gold",
  IN_PROGRESS: "blue",
  COMPLETED: "green",
  REJECTED: "red",
};

function displayName(emp) {
  if (!emp) return "";
  return [emp.salutation, emp.first_name, emp.last_name].filter(Boolean).join(" ").trim();
}

// Approver objects may come back as a nested user/employee record (first_name,
// last_name, emp_code) — this normalizes whatever shape we get into a label.
function approverName(approver) {
  if (!approver) return "";
  if (typeof approver === "string") return approver;
  const name = displayName(approver) || approver.name || approver.username || "";
  return name || approver.emp_code || "";
}

// Fallback: pull the most recent APPROVER_ASSIGNED log entry for a given
// clearance_type and use its actor as the assigned approver, in case the
// clearance object itself doesn't include an `approver` field.
function approverFromLogs(logs, clearanceType) {
  if (!Array.isArray(logs)) return null;
  const entry = logs.find(
    (log) => log.clearance_type === clearanceType && log.action === "APPROVER_ASSIGNED"
  );
  return entry?.actor ?? entry?.assigned_to ?? entry?.target ?? null;
}

function toDayjs(value) {
  return value ? dayjs(value) : null;
}

function fromDayjs(value) {
  return value ? value.format("YYYY-MM-DD") : null;
}

function ExitInterviewHrPanel({ loading, triggering, exitInterview, onTrigger }) {
  if (loading) {
    return <Spin />;
  }

  if (!exitInterview) {
    return (
      <div className="paper-field-row" style={{ textAlign: "center", padding: "32px 0" }}>
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          The exit interview hasn't been triggered for this employee yet. Once
          triggered, it will appear on their login for them to fill in.
        </Text>
        <Button type="primary" loading={triggering} onClick={onTrigger}>
          Trigger Exit Interview
        </Button>
      </div>
    );
  }

  const status = exitInterview.status;

  return (
    <div>
      <Row justify="space-between" align="middle" className="paper-field-row">
        <Col>
          <Tag color={EI_STATUS_COLOR[status] || "default"}>{status?.replace(/_/g, " ")}</Tag>
          {exitInterview.submitted_at && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              Submitted {dayjs(exitInterview.submitted_at).format("DD MMM YYYY, h:mm A")}
            </Text>
          )}
        </Col>
      </Row>

      <div className="paper-id-block">
        <IdRow label="Name" value={exitInterview.employee_name_snapshot || "—"} />
        <IdRow label="HOD" value={exitInterview.hod_name_snapshot || "—"} />
        <IdRow label="Emp No." value={exitInterview.emp_no_snapshot || "—"} />
        <IdRow label="Position" value={exitInterview.current_position_snapshot || "—"} />
      </div>

      {exitInterview.status === "TRIGGERED" && (!exitInterview.answers || exitInterview.answers.length === 0) ? (
        <Text type="secondary">The employee hasn't started answering yet.</Text>
      ) : (
        <div className="paper-field-row">
          {exitInterview.answers.map((a, idx) => (
            <div key={a.id} style={{ marginBottom: 16 }}>
              <FieldLabel>{`Q${idx + 1}. ${a.question.question_text}`}</FieldLabel>
              <Text>{formatAnswer(a)}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAnswer(answer) {
  const { question, text_answer, selected_options, other_text, rating_value, yes_no_value } = answer;
  switch (question.question_type) {
    case "RATING":
      return rating_value ? `${rating_value} / 5` : "—";
    case "YES_NO":
      return yes_no_value === true ? "Yes" : yes_no_value === false ? "No" : "—";
    case "SINGLE_CHOICE":
    case "MULTI_CHOICE": {
      const opts = selected_options?.length ? selected_options.join(", ") : "—";
      return other_text ? `${opts} (Other: ${other_text})` : opts;
    }
    default:
      return text_answer || "—";
  }
}

export default function ClearanceFormPreview({
  open,
  onClose,
  exitProcessId,
  employee,
  resignation,
  onSaved,
}) {
  const { authData, backUrl } = useContext(AuthContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [finalApproval, setFinalApproval] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [handoverOptions, setHandoverOptions] = useState([]);

  const [exitInterview, setExitInterview] = useState(null); // null = not triggered
  const [eiLoading, setEiLoading] = useState(false);
  const [eiTriggering, setEiTriggering] = useState(false);

  // Load the exit interview (if triggered) whenever the drawer opens, same
  // pattern as the clearance form fetch above.
  useEffect(() => {
    if (!open || !exitProcessId || !authData?.access) return;
    let cancelled = false;
    setEiLoading(true);

    axios
      .get(`${backUrl}${EXIT_INTERVIEW_DETAIL_PATH(exitProcessId)}`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      })
      .then((res) => !cancelled && setExitInterview(res.data))
      .catch((err) => {
        if (err.response?.status === 404) {
          if (!cancelled) setExitInterview(null); // not triggered yet
        } else {
          console.error("Error loading exit interview:", err);
        }
      })
      .finally(() => !cancelled && setEiLoading(false));

    return () => {
      cancelled = true;
    };
  }, [open, exitProcessId, authData?.access, backUrl]);

  async function handleTriggerExitInterview() {
    if (!exitProcessId) {
      messageApi.error("This employee doesn't have an exit process yet.");
      return;
    }
    setEiTriggering(true);
    try {
      const res = await axios.post(
        `${backUrl}${EXIT_INTERVIEW_TRIGGER_PATH(exitProcessId)}`,
        {},
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setExitInterview(res.data);
      messageApi.success("Exit interview triggered. The employee can now fill it in from their login.");
    } catch (err) {
      console.error("Error triggering exit interview:", err);
      messageApi.error(err.response?.data?.detail || "Could not trigger the exit interview.");
    } finally {
      setEiTriggering(false);
    }
  }

  const [form, setForm] = useState({
    resignation_date: resignation?.date ?? null,
    relieving_date: resignation?.requested_last_working_day ?? null,
    address_for_correspondence: "",
    phone_for_correspondence: "",
    department_head: {
      items: DEFAULT_ITEMS.department_head,
      handed_over_to_id: null,
      employee_signature_date: null,
      handover_person_signature_date: null,
      dept_head_signature_date: null,
      remarks: "",
      approver: null,
      status: null,
    },
    it: { items: DEFAULT_ITEMS.it, signature_date: null, remarks: "", approver: null, status: null },
    finance: { items: DEFAULT_ITEMS.finance, signature_date: null, remarks: "", approver: null, status: null },
    hr: {
      items: DEFAULT_ITEMS.hr,
      exit_interview_completed: false,
      signature_date: null,
      remarks: "",
      approver: null,
      status: null,
    },
  });

  // Pull in whatever is already saved (if this exit process already has a
  // partially filled form) so the preview never overwrites existing work.
  useEffect(() => {
    if (!open || !exitProcessId || !authData?.access) return;
    let cancelled = false;
    setLoading(true);

    axios
      .get(`${backUrl}${CLEARANCE_FORM_PATH(exitProcessId)}`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      })
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};

        setFinalApproval(d.final_approval ?? null);
        const logs = d.approval_logs || [];

        setForm((prev) => ({
          ...prev,
          resignation_date: d.resignation_date ?? prev.resignation_date,
          relieving_date: d.relieving_date ?? prev.relieving_date,
          address_for_correspondence: d.address_for_correspondence ?? "",
          phone_for_correspondence: d.phone_for_correspondence ?? "",
          department_head: {
            items: d.department_head_clearance?.items?.length
              ? d.department_head_clearance.items
              : DEFAULT_ITEMS.department_head,
            handed_over_to_id: d.department_head_clearance?.handed_over_to?.id ?? null,
            employee_signature_date: d.department_head_clearance?.employee_signature_date ?? null,
            handover_person_signature_date:
              d.department_head_clearance?.handover_person_signature_date ?? null,
            dept_head_signature_date: d.department_head_clearance?.dept_head_signature_date ?? null,
            remarks: d.department_head_clearance?.remarks ?? "",
            approver:
              d.department_head_clearance?.approver ??
              approverFromLogs(logs, CLEARANCE_TYPE_MAP.department_head),
            status: d.department_head_clearance?.status ?? null,
          },
          it: {
            items: d.it_clearance?.items?.length ? d.it_clearance.items : DEFAULT_ITEMS.it,
            signature_date: d.it_clearance?.signature_date ?? null,
            remarks: d.it_clearance?.remarks ?? "",
            approver: d.it_clearance?.approver ?? approverFromLogs(logs, CLEARANCE_TYPE_MAP.it),
            status: d.it_clearance?.status ?? null,
          },
          finance: {
            items: d.finance_clearance?.items?.length ? d.finance_clearance.items : DEFAULT_ITEMS.finance,
            signature_date: d.finance_clearance?.signature_date ?? null,
            remarks: d.finance_clearance?.remarks ?? "",
            approver: d.finance_clearance?.approver ?? approverFromLogs(logs, CLEARANCE_TYPE_MAP.finance),
            status: d.finance_clearance?.status ?? null,
          },
          hr: {
            items: d.hr_clearance?.items?.length ? d.hr_clearance.items : DEFAULT_ITEMS.hr,
            exit_interview_completed: d.hr_clearance?.exit_interview_completed ?? false,
            signature_date: d.hr_clearance?.signature_date ?? null,
            remarks: d.hr_clearance?.remarks ?? "",
            approver: d.hr_clearance?.approver ?? approverFromLogs(logs, CLEARANCE_TYPE_MAP.hr),
            status: d.hr_clearance?.status ?? null,
          },

        }));
      })
      .catch((err) => {
        // A 404 here just means the form hasn't been started yet — the
        // defaults above are fine, so only surface real failures.
        if (err.response?.status !== 404) {
          console.error("Error loading clearance form:", err);
          messageApi.error("Could not load the existing clearance form.");
        }
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exitProcessId, authData?.access, backUrl]);

  // Employees eligible for "handed over to" — same department as the
  // resigning employee, since work usually passes to a department peer.
  useEffect(() => {
    if (!open || !authData?.access || !employee?.department) return;
    axios
      .get(EMPLOYEES_PATH.replace(/^/, backUrl), {
        params: { department: employee.department },
        headers: { Authorization: `Bearer ${authData.access}` },
      })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setHandoverOptions(
          list
            .filter((e) => e.id !== employee?.id)
            .map((e) => ({ value: e.id, label: displayName(e) || e.emp_code }))
        );
      })
      .catch(() => setHandoverOptions([]));
  }, [open, authData?.access, backUrl, employee?.department, employee?.id]);

  function patchSection(section, patch) {
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));
  }

  async function handleSave() {
    if (!exitProcessId) {
      messageApi.error("This employee doesn't have an exit process yet.");
      return;
    }
    setSaving(true);
    const payload = {
      resignation_date: form.resignation_date,
      relieving_date: form.relieving_date,
      address_for_correspondence: form.address_for_correspondence,
      phone_for_correspondence: form.phone_for_correspondence,
      department_head: {
        items: form.department_head.items.map(({ id, sr_no, particulars, status }) => ({
          ...(id ? { id } : {}),
          sr_no,
          particulars,
          status,
        })),
        handed_over_to_id: form.department_head.handed_over_to_id,
        employee_signature_date: form.department_head.employee_signature_date,
        handover_person_signature_date: form.department_head.handover_person_signature_date,
        dept_head_signature_date: form.department_head.dept_head_signature_date,
        remarks: form.department_head.remarks,
      },
      it: {
        items: form.it.items.map(({ id, sr_no, particulars, status, remarks }) => ({
          ...(id ? { id } : {}),
          sr_no,
          particulars,
          status,
          remarks,
        })),
        signature_date: form.it.signature_date,
        remarks: form.it.remarks,
      },
      finance: {
        items: form.finance.items.map(({ id, sr_no, item_name, amount_to_be_deducted, remarks }) => ({
          ...(id ? { id } : {}),
          sr_no,
          item_name,
          amount_to_be_deducted,
          remarks,
        })),
        signature_date: form.finance.signature_date,
        remarks: form.finance.remarks,
      },
      hr: {
        items: form.hr.items.map(({ id, sr_no, particulars, status, remarks }) => ({
          ...(id ? { id } : {}),
          sr_no,
          particulars,
          status,
          remarks,
        })),
        exit_interview_completed: form.hr.exit_interview_completed,
        signature_date: form.hr.signature_date,
        remarks: form.hr.remarks,
      },
    };

    try {
      await axios.post(`${backUrl}${CLEARANCE_FORM_PATH(exitProcessId)}`, payload, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      messageApi.success("Clearance form saved.");
      onSaved?.(payload);
      onClose?.();
    } catch (err) {
      console.error("Error saving clearance form:", err);
      messageApi.error(err.response?.data?.detail || "Could not save the clearance form.");
    } finally {
      setSaving(false);
    }
  }

  // Downloads the clearance form as a PDF, formatted exactly like the
  // original printed Employee Clearance Form. Reflects whatever is
  // currently saved on the server (not unsaved edits in the drawer), so
  // the "Save all" button hint below nudges the user to save first.
  async function handleDownloadPdf() {
    if (!exitProcessId) {
      messageApi.error("This employee doesn't have an exit process yet.");
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.get(`${backUrl}${CLEARANCE_FORM_PDF_PATH(exitProcessId)}`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const empCode = employee?.emp_code || exitProcessId;
      const link = document.createElement("a");
      link.href = url;
      link.download = `Clearance_Form_${empCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading clearance form PDF:", err);
      messageApi.error(err.response?.data?.detail || "Could not download the clearance form.");
    } finally {
      setDownloading(false);
    }
  }

  const emp = employee || {};

  return (
    <Drawer
      title={null}
      open={open}
      onClose={onClose}
      width="min(880px, 100vw)"
      className="clearance-drawer"
      styles={{ body: { padding: 0, background: "#eceeec" } }}
      footer={
        <div className="clearance-drawer-footer">
          <Text type="secondary" className="clearance-footer-hint">
            This mirrors the printed Employee Clearance Form — fill in each
            section, save, then download the PDF.
          </Text>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button icon={<FilePdfOutlined />} loading={downloading} onClick={handleDownloadPdf}>
              Download PDF
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Save all
            </Button>
          </Space>
        </div>
      }
    >
      {contextHolder}
      <Spin spinning={loading}>
        <div className="clearance-drawer-toolbar">
          <FilePdfOutlined />
          <span>Clearance form preview</span>
          {finalApproval && (
            <Tag
              color={FINAL_APPROVAL_STATUS_COLOR[finalApproval.status] || "default"}
              style={{ marginLeft: "auto" }}
            >
              Final approval: {finalApproval.status}
              {finalApproval.decided_by ? ` by ${finalApproval.decided_by.first_name || finalApproval.decided_by.emp_code}` : ""}
            </Tag>
          )}
        </div>

        <Tabs
          className="clearance-tabs"
          defaultActiveKey="page1"
          items={[
            {
              key: "page1",
              label: "1 · Department Head",
              children: (
                <PaperPage pageLabel="1 | Page">
                  <FormHeader company={COMPANY_NAME} title={FORM_TITLE} />

                  <Row gutter={16} className="paper-dates-row">
                    <Col span={12}>
                      <FieldLabel>Resignation Date</FieldLabel>
                      <DatePicker
                        className="paper-input"
                        value={toDayjs(form.resignation_date)}
                        onChange={(v) => setForm((p) => ({ ...p, resignation_date: fromDayjs(v) }))}
                        format="DD MMM YYYY"
                      />
                    </Col>
                    <Col span={12}>
                      <FieldLabel>Relieving Date</FieldLabel>
                      <DatePicker
                        className="paper-input"
                        value={toDayjs(form.relieving_date)}
                        onChange={(v) => setForm((p) => ({ ...p, relieving_date: fromDayjs(v) }))}
                        format="DD MMM YYYY"
                      />
                    </Col>
                  </Row>

                  <SectionTitle
                    approver={form.department_head.approver}
                    status={form.department_head.status}
                  >
                    Clearance From Dept Head
                  </SectionTitle>

                  <EditableItemsTable
                    addLabel="Add particular"
                    rows={form.department_head.items}
                    onChange={(items) => patchSection("department_head", { items })}
                    columns={[
                      { key: "particulars", label: "Particulars", type: "text", placeholder: "e.g. Pending project handover" },
                      { key: "status", label: "Status", type: "select", width: 150, options: ITEM_STATUS_OPTIONS },
                    ]}
                  />

                  <Row gutter={16} className="paper-field-row">
                    <Col span={24}>
                      <FieldLabel>Handing Over Done To (Name)</FieldLabel>
                      <Select
                        className="paper-input"
                        style={{ width: "100%" }}
                        allowClear
                        showSearch
                        placeholder="Select employee"
                        options={handoverOptions}
                        value={form.department_head.handed_over_to_id}
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(v) => patchSection("department_head", { handed_over_to_id: v ?? null })}
                      />
                    </Col>
                  </Row>

                  <SignatureRow
                    label="Employee Signature"
                    value={form.department_head.employee_signature_date}
                    onChange={(v) => patchSection("department_head", { employee_signature_date: fromDayjs(v) })}
                  />
                  <SignatureRow
                    label="Signature of Person Handed Over To"
                    value={form.department_head.handover_person_signature_date}
                    onChange={(v) =>
                      patchSection("department_head", { handover_person_signature_date: fromDayjs(v) })
                    }
                  />
                  <SignatureRow
                    label="Signature of Departmental Head"
                    value={form.department_head.dept_head_signature_date}
                    onChange={(v) => patchSection("department_head", { dept_head_signature_date: fromDayjs(v) })}
                  />

                  <IdBlock employee={emp} />
                </PaperPage>
              ),
            },
            {
              key: "page2",
              label: "2 · IT & Finance",
              children: (
                <PaperPage pageLabel="2 | Page">
                  <FormHeader company={COMPANY_NAME} title={FORM_TITLE} />

                  <SectionTitle approver={form.it.approver} status={form.it.status}>
                    Clearance From — IT
                  </SectionTitle>
                  <EditableItemsTable
                    addLabel="Add item"
                    rows={form.it.items}
                    onChange={(items) => patchSection("it", { items })}
                    columns={[
                      { key: "particulars", label: "List of Items", type: "text" },
                      { key: "status", label: "Status", type: "select", width: 150, options: RETURN_OPTIONS },
                    ]}
                  />
                  <SignatureRow
                    label="Signature (IT)"
                    value={form.it.signature_date}
                    onChange={(v) => patchSection("it", { signature_date: fromDayjs(v) })}
                  />

                  <Divider className="paper-divider" />

                  <SectionTitle approver={form.finance.approver} status={form.finance.status}>
                    Clearance From Finance
                  </SectionTitle>
                  <EditableItemsTable
                    addLabel="Add item"
                    rows={form.finance.items}
                    onChange={(items) => patchSection("finance", { items })}
                    columns={[
                      { key: "item_name", label: "Item Name", type: "text" },
                      { key: "amount_to_be_deducted", label: "Amt. to be deducted in F&F", type: "number", width: 200 },
                    ]}
                  />
                  <SignatureRow
                    label="Signature (Finance)"
                    value={form.finance.signature_date}
                    onChange={(v) => patchSection("finance", { signature_date: fromDayjs(v) })}
                  />
                </PaperPage>
              ),
            },
            {
              key: "page3",
              label: "3 · HR",
              children: (
                <PaperPage pageLabel="3 | Page">
                  <FormHeader company={COMPANY_NAME} title={FORM_TITLE} />

                  <SectionTitle approver={form.hr.approver} status={form.hr.status}>
                    Clearance From HR
                  </SectionTitle>
                  <EditableItemsTable
                    addLabel="Add item"
                    rows={form.hr.items}
                    onChange={(items) => patchSection("hr", { items })}
                    columns={[
                      { key: "particulars", label: "List of Items", type: "text" },
                      { key: "status", label: "Status", type: "select", width: 150, options: RETURN_OPTIONS },
                    ]}
                  />

                  <div className="paper-note">
                    Clearance from HR to be given only after obtaining all the above
                    clearances and completion of exit interview.
                  </div>

                  {/* <Checkbox
                    checked={form.hr.exit_interview_completed}
                    onChange={(e) => patchSection("hr", { exit_interview_completed: e.target.checked })}
                    className="paper-checkbox"
                  >
                    Exit interview completed
                  </Checkbox> */}

                  <SignatureRow
                    label="Signature (HR)"
                    value={form.hr.signature_date}
                    onChange={(v) => patchSection("hr", { signature_date: fromDayjs(v) })}
                  />

                  <SectionTitle>Address &amp; Phone No. for Correspondence</SectionTitle>
                  <Text type="secondary" className="paper-hint">
                    To be given by the employee.
                  </Text>
                  <TextArea
                    className="paper-input paper-textarea"
                    rows={3}
                    placeholder="Address for further correspondence"
                    value={form.address_for_correspondence}
                    onChange={(e) => setForm((p) => ({ ...p, address_for_correspondence: e.target.value }))}
                  />
                  <Input
                    className="paper-input"
                    style={{ marginTop: 8, maxWidth: 260 }}
                    placeholder="Phone number"
                    value={form.phone_for_correspondence}
                    onChange={(e) => setForm((p) => ({ ...p, phone_for_correspondence: e.target.value }))}
                  />
                </PaperPage>
              ),
            },
            {
              key: "exit-interview",
              label: "4 · Exit Interview",
              children: (
                <PaperPage pageLabel="4 | Page">
                  <FormHeader company={COMPANY_NAME} title="Exit Interview Questionnaire" />
                  <ExitInterviewHrPanel
                    loading={eiLoading}
                    triggering={eiTriggering}
                    exitInterview={exitInterview}
                    onTrigger={handleTriggerExitInterview}
                  />
                </PaperPage>
              ),
            },
          ]}
        />
      </Spin>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Small paper-form building blocks
// ---------------------------------------------------------------------------

function PaperPage({ children, pageLabel }) {
  return (
    <div className="paper-page">
      <div className="paper-sheet">{children}</div>
      <div className="paper-page-number">{pageLabel}</div>
    </div>
  );
}

function FormHeader({ company, title }) {
  return (
    <div className="paper-form-header">
      <div className="paper-company">{company}</div>
      <div className="paper-form-title">{title}</div>
    </div>
  );
}

// Section title now optionally shows who is assigned to approve this
// section, plus the section's current status (e.g. COMPLETED, PENDING).
function SectionTitle({ children, approver, status }) {
  const name = approverName(approver);
  return (
    <div className="paper-section-title-row">
      <div className="paper-section-title">{children}</div>
      {(name || status) && (
        <Space size={6} className="paper-section-approver">
          {name && (
            <Tag icon={<UserOutlined />} className="paper-approver-tag">
              Approver: {name}
            </Tag>
          )}
          {status && (
            <Tag color={STATUS_TAG_COLOR[status] || "default"}>
              {status.replace(/_/g, " ")}
            </Tag>
          )}
        </Space>
      )}
    </div>
  );
}

function FieldLabel({ children }) {
  return <div className="paper-field-label">{children}</div>;
}

function SignatureRow({ label, value, onChange }) {
  return (
    <Row className="paper-signature-row" align="middle" gutter={12}>
      <Col flex="1 1 auto">
        <Text>{label}:</Text>
      </Col>
      <Col flex="0 0 200px">
        <DatePicker
          size="small"
          className="paper-input"
          style={{ width: "100%" }}
          placeholder="Date"
          value={toDayjs(value)}
          onChange={onChange}
          format="DD MMM YYYY"
        />
      </Col>
    </Row>
  );
}

function IdBlock({ employee }) {
  return (
    <div className="paper-id-block">
      <IdRow label="Name" value={displayName(employee) || "—"} />
      <IdRow label="Emp No." value={employee?.emp_code || "—"} />
      <IdRow label="Designation" value={employee?.designation_name || "—"} />
      <IdRow label="Dept" value={employee?.department_name || "—"} />
      <IdRow
        label="Joining Date"
        value={employee?.date_of_joining ? dayjs(employee.date_of_joining).format("DD MMM YYYY") : "—"}
      />
    </div>
  );
}

function IdRow({ label, value }) {
  return (
    <div className="paper-id-row">
      <span className="paper-id-label">{label}:</span>
      <span className="paper-id-value">{value}</span>
    </div>
  );
}